import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, Instagram, Linkedin } from "lucide-react";
import { useAuth } from "@/auth/useAuth";
import { useTranslation } from "react-i18next";
import { useStoreSettings, type DaySchedule } from "@/lib/hooks/useStoreSettings";

interface UserShape {
  is_admin: boolean;
}

// ---------------------------------------------------------------------------
// Working-hours helpers (footer-specific — compact format)
// ---------------------------------------------------------------------------

function footerTimeSlots(schedule: DaySchedule): string | null {
  if (schedule.closed || schedule.slots.length === 0) return null;
  return schedule.slots.map((s) => `${s.open}–${s.close}`).join(" | ");
}

const COMPANY_EMAIL = "geral@dm-3dprinting.com";
const COMPANY_PHONE = "+351 937 427 191";
// --- START EDIT: Add second phone number constant ---
const COMPANY_PHONE_2 = "+351 917 370 007";
// --- END EDIT: Add second phone number constant ---
const COMPANY_ADDRESS = "Rua de Tomaz Ribeiro 733, 4450-298, Matosinhos, Portugal";
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/2aSfsJVzD7LkGTgL8";
const INSTAGRAM_URL = "https://instagram.com/dm.3dprinting";
const LINKEDIN_URL = "https://www.linkedin.com/company/dm3d-printing/";
const TIKTOK_URL = "https://www.tiktok.com/@dm3dprinting";

function TikTokIcon({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1V9.01a6.34 6.34 0 0 0-.79-.05 6.34 6.34 0 0 0-6.34 6.34 6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.33-6.34V8.69a8.27 8.27 0 0 0 4.83 1.54V6.79a4.85 4.85 0 0 1-1.06-.1z" />
    </svg>
  );
}

export default function Footer() {
  const { user } = useAuth();
  const { t } = useTranslation('footer');
  const u = user as UserShape | undefined;
  const isAdmin = u?.is_admin ?? false;
  const isLoggedIn = !!u;
  const { settings: storeSettings } = useStoreSettings();

  // --- START EDIT: Helper to format tel hrefs consistently ---
  const telHref = (num: string) => `tel:${num.replace(/\s/g, "")}`;
  // --- END EDIT: Helper to format tel hrefs consistently ---

  return (
    <footer className="border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 lg:py-10">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-3 lg:grid-cols-5">
          {/* Contact Info */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('contactUs')}</h3>
            <ul className="space-y-3 text-sm text-muted-foreground">
              <li>
                <a
                  href={`mailto:${COMPANY_EMAIL}`}
                  className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  <Mail className="h-4 w-4" />
                  {COMPANY_EMAIL}
                </a>
              </li>
              <li>
                <a
                  href={telHref(COMPANY_PHONE)}
                  className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {COMPANY_PHONE}
                </a>
              </li>
              <li>
                <a
                  href={telHref(COMPANY_PHONE_2)}
                  className="inline-flex items-center gap-2 hover:text-foreground transition-colors"
                >
                  <Phone className="h-4 w-4" />
                  {COMPANY_PHONE_2}
                </a>
              </li>
              <li>
                <a
                  href={GOOGLE_MAPS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-start gap-2 hover:text-foreground transition-colors"
                >
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{COMPANY_ADDRESS}</span>
                </a>
              </li>
            </ul>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('quickLinks')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('home')}
                </Link>
              </li>
              <li>
                <Link to="/portfolio" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('portfolio')}
                </Link>
              </li>
              <li>
                <Link to="/material-guide" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('materialGuide')}
                </Link>
              </li>
              <li>
                <Link to="/contact" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('contact')}
                </Link>
              </li>
              {/* Hide user-only links for admins */}
              {!isAdmin && (
                <>
                  <li>
                    <Link to="/upload" className="text-muted-foreground hover:text-foreground transition-colors">
                      {t('uploadAndPrint')}
                    </Link>
                  </li>
                  {isLoggedIn && (
                    <>
                      <li>
                        <Link to="/account/orders" className="text-muted-foreground hover:text-foreground transition-colors">
                          {t('myOrders')}
                        </Link>
                      </li>
                      <li>
                        <Link to="/account/company" className="text-muted-foreground hover:text-foreground transition-colors">
                          {t('company')}
                        </Link>
                      </li>
                    </>
                  )}
                </>
              )}
              {/* Admin-only links */}
              {isAdmin && (
                <li>
                  <Link to="/admin" className="text-muted-foreground hover:text-foreground transition-colors">
                    {t('adminOrders')}
                  </Link>
                </li>
              )}
            </ul>
          </div>

          {/* Working Hours */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('workingHours')}</h3>
            {storeSettings?.working_hours && storeSettings.working_hours.length > 0 ? (
              <ul className="space-y-0.5 text-sm text-muted-foreground">
                {storeSettings.working_hours.map((schedule) => {
                  const slots = footerTimeSlots(schedule);
                  return (
                    <li key={schedule.day} className="flex gap-3">
                      <span className="w-7 shrink-0 font-medium text-foreground">
                        {t(`days.${schedule.day}`, schedule.day)}
                      </span>
                      <span className={!slots ? "italic" : ""}>
                        {slots ?? t('closed')}
                      </span>
                    </li>
                  );
                })}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground italic">—</p>
            )}
          </div>

          {/* Social */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('followUs')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Instagram className="h-4 w-4" />
                  {t('instagram')}
                </a>
              </li>
              <li>
                <a
                  href={LINKEDIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <Linkedin className="h-4 w-4" />
                  {t('linkedin')}
                </a>
              </li>
              <li>
                <a
                  href={TIKTOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <TikTokIcon className="h-4 w-4" />
                  {t('tiktok')}
                </a>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold">{t('legal')}</h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/privacy" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('privacyPolicy')}
                </Link>
              </li>
              <li>
                <Link to="/terms" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('termsOfService')}
                </Link>
              </li>
              <li>
                <Link to="/cookies" className="text-muted-foreground hover:text-foreground transition-colors">
                  {t('cookiePolicy')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="mt-8 border-t pt-5">
          <p className="text-center text-sm text-muted-foreground">
            {t('copyright', { year: new Date().getFullYear() })}
          </p>
        </div>
      </div>
    </footer>
  );
}